import { TranCategory } from '@helios/common-api'
import {
  BulkCashEntryTransaction,
  createMetadata,
  getDefaultUnknownCounterParty,
  getTransactionTypeKeyFromCode,
  MetaDataTransaction,
  TransactionRequestWrapper,
} from '@helios/create-transactions-modal'
import {
  Distribution,
  QuickEnterPredictionAction,
} from '../models/distribution.model'
/* eslint-disable */
import {
  CashEntryDataMap,
  CashEntryTransaction,
} from 'libs/create-transactions-modal/src/lib/model/single-transaction/cash-entry/cash-entry-transaction.model'
import { CreateEditTransactionRequest } from 'libs/create-transactions-modal/src/lib/model/single-transaction/create-edit-transaction.model'
import { RequestMode } from 'libs/create-transactions-modal/src/lib/model/validation-response.model'
import { TransactionConstants } from 'libs/create-transactions-modal/src/lib/util/constants'
/* eslint-enable */

/**
 * Creates a transaction command for a single entry.
 *
 * @param  data - The quick enter prediction action object.
 * @returns  - The created transaction command.
 */
export function createTranCommandForSinglePredictionEntry(
  data: QuickEnterPredictionAction,
): CreateEditTransactionRequest {
  const dist = data.distributionClicked[0]
  const transaction: CashEntryTransaction = createCashEntryTransaction(
    dist,
    data.userId,
  )
  return {
    tranCategory: TranCategory.CASH_ENTRY,
    metadata: createMetadata(
      data.sessionReconDate ?? new Date(),
      dist.assetId.toString(),
    ),
    transactionNote: { newValue: 'As per distributions panel' },
    transaction: transaction,
    notes: TransactionConstants.HELIOS_CASH_ENTRY_SAVE_NOTE_CW_CREATE,
  }
}

/**
 * Creates a transaction command for bulk entry.
 *
 * @param  data - The quick enter prediction action object.
 * @returns - The created transaction command.
 */
export function createTranCommandForBulkPredictionEntry(
  data: QuickEnterPredictionAction,
) {
  const transactions = mapDistributionForQuickEnterPrediction(
    data.distributionClicked,
    data.sessionReconDate,
    data.userId,
  )
  return {
    tranCategory: TranCategory.CASH_ENTRY,
    requestMode: RequestMode.NEW_TRAN,
    transaction: transactions,
    transactionNote: { newValue: 'As per distributions panel' },
    notes: TransactionConstants.HELIOS_BULK_CASH_ENTRY_SAVE_NOTE_CW_CREATE,
  }
}

/**
 * Creates a bulk cash entry transaction based on the given distribution and user ID.
 *
 * @param dist - The distribution object.
 * @param userId - (Optional) The user ID.
 * @returns The created bulk cash entry transaction.
 */
function createBulkCashEntryTransaction(
  dist: Distribution,
  userId?: number,
): BulkCashEntryTransaction {
  const defaultUnknownCounterParty = getDefaultUnknownCounterParty()
  const currentDate = new Date()

  const dataMap: CashEntryDataMap = {}

  if (dist.calculatedPrepaidInterest) {
    dataMap.prepaidInterest = dist.calculatedPrepaidInterest
  }

  return {
    tranType: getTransactionTypeKeyFromCode(dist.tranTypeCode),
    entryDate: dist.calculatedEntryDate ?? currentDate.toString(),
    settleDate: dist.calculatedSettleDate ?? currentDate.toString(),
    postDate: dist.calculatedPostDate ?? currentDate.toString(),
    amount: dist.calculatedAmount ?? 0,
    securityId: dist.secId,
    accountId: dist.acctId,
    createdDate: new Date().toISOString(),
    modifiedDate: new Date().toISOString(),
    userId: userId ?? 0,
    sourceId: TransactionConstants.HELIOS_SOURCE_ID,
    active: true,
    dataMap: dataMap,
    revisionId: 1,
    factor: 1,
    traderId: 0,
    commission: 0,
    counterParty: defaultUnknownCounterParty,
    counterPartyId: defaultUnknownCounterParty.id,
  }
}

/**
 * Maps distributions to transaction request wrappers for quick enter prediction.
 *
 * @param  distributions - The list of distributions.
 * @param  sessionReconDate - The session reconciliation date.
 * @param  userId - The user ID.
 * @returns - The mapped transaction request wrappers.
 */
function mapDistributionForQuickEnterPrediction(
  distributions: Distribution[],
  sessionReconDate?: Date,
  userId?: number,
): TransactionRequestWrapper[] {
  const transactions: TransactionRequestWrapper[] = []
  distributions.forEach((dist, index) => {
    const transactionData: BulkCashEntryTransaction =
      createBulkCashEntryTransaction(dist, userId)
    const metaData: MetaDataTransaction = {
      rowId: index + 1,
      reconDate: sessionReconDate ?? new Date(),
      cusip: dist.assetId,
    }
    transactions.push({ transactionData, metaData })
  })
  return transactions
}

/**
 * Creates a cash entry transaction based on the given distribution and user ID.
 *
 * @param dist - The distribution object.
 * @param userId - (Optional) The user ID.
 * @returns The created cash entry transaction.
 */
function createCashEntryTransaction(
  dist: Distribution,
  userId?: number,
): CashEntryTransaction {
  const dataMap: CashEntryDataMap = {}

  if (dist.calculatedPrepaidInterest) {
    dataMap.prepaidInterest = dist.calculatedPrepaidInterest
  }
  dataMap.collateralBroker = dist.collateralBroker ?? ''

  return {
    tranType: getTransactionTypeKeyFromCode(dist.tranTypeCode),
    entryDate: dist.calculatedEntryDate ?? new Date().toString(),
    settleDate: dist.calculatedSettleDate ?? new Date().toString(),
    postDate: dist.calculatedPostDate ?? new Date().toString(),
    amount: dist.calculatedAmount ?? 0,
    securityId: dist.secId,
    accountId: dist.acctId,
    createdDate: new Date().toISOString(),
    modifiedDate: new Date().toISOString(),
    userId: userId ?? 0,
    sourceId: TransactionConstants.HELIOS_SOURCE_ID,
    active: true,
    dataMap: dataMap,
    revisionId: 1,
  }
}
